
import * as THREE from 'three';
import { ThreeView } from '../View3dWebCad';

/// object : box = 1 : 1
export class Box3Status {
    constructor(public object: THREE.Object3D, public box: THREE.Box3) {
    }
}

// node管理
export class Node {
    public parent: Node;
    public children: Node[];
    public box3Status: Box3Status[];
    public box: THREE.Box3;

    constructor() {
        this.box3Status = null;
        this.box = null;
        this.parent  = null;
        this.children = [];
    }

    public set(_box3Status: Box3Status[], _box: THREE.Box3) {
        this.box3Status = _box3Status;
        this.box = _box;
    }

    public addChild(_status: Node) {
        _status.parent = this;
        this.children.push(_status);
    }
}


export class OctreeManager {

    public nodeStatus: Node;
    private infos: Box3Status[];
    private threshold: number = 10; // しきい値
    private limitLevel: number = 4; // 深さの限界値
    private globalSpace: THREE.Box3;

    constructor(private objects: THREE.Object3D[]) {
        this.nodeStatus = new Node();
        this.infos = [];

        // データ登録
        this.globalSpace = new THREE.Box3();
        this.regist();

        // treeの親を登録
        this.nodeStatus.set([], this.globalSpace);

        // tree構造を作成
        // console.profile('create tree test');
        const _level = 0;
        this.createTree(_level, this.nodeStatus, this.infos);
        // console.profileEnd('create tree test');

        console.log(this.nodeStatus);

    }

    // データ登録
    private regist(): void {
        this.objects.forEach((obj: THREE.Object3D) => {
            const bbox = new THREE.Box3().setFromObject(obj);
            // bboxのマージ
            this.globalSpace.union(bbox);
            // データをセット
            this.infos.push({
                box: bbox,
                object: obj
            });
        });
    }

    // tree構造を作成
    private createTree(_level: number, _node: Node, _infos: Box3Status[]) {
        _level += 1;

        // 深さの限界値以上は分割しない
        if (_level > this.limitLevel) {
            _node.box3Status = _infos;
            return;
        }

        // しきい値より少ない場合は分割しない
        if (_infos.length < this.threshold) {
            _node.box3Status = _infos;
            return;
        }

        // 空間を8つに分割
        const spaces = this.spacePlace(_node);
        if (spaces == undefined) return;

        // BBOX同士の当たり判定
        for(let i = 0; i < spaces.length; i++) {
            const infos = [];
            for (let j = 0; j < _infos.length; j++) {
                if (spaces[i].intersectsBox(_infos[j].box)) {
                    infos.push(_infos[j]);
                }
            }
            if (infos.length < 1) continue;

            const node = new Node();
            node.set([], spaces[i]);
            _node.addChild(node);

            this.createTree(_level, node, infos);
        }
    }

    private _v1 = new THREE.Vector3();
    private _v2 = new THREE.Vector3();

    private createSubTree(_level: number, _node: Node, _infos: Box3Status[]) {
        _level += 1;

        // 深さの限界値以上は分割しない
        // しきい値より少ない場合は分割しない
        if (_level > this.limitLevel || _infos.length < this.threshold) {
            _node.box3Status = _infos;
            return;
        }

        const halfsize = this._v2.copy( _node.box.max ).sub( _node.box.min ).multiplyScalar( 0.5 );

        // 空間を８分割する
        for ( let x = 0; x < 2; x ++ ) {
            for ( let y = 0; y < 2; y ++ ) {
                for ( let z = 0; z < 2; z ++ ) {
                    const box = new THREE.Box3();
                    const v = this._v1.set( x, y, z );
                    box.min.copy( _node.box.min ).add( v.multiply( halfsize ) );
                    box.max.copy( box.min ).add( halfsize );

                    // BBOX同士の当たり判定
                    const infos = [];
                    for (let i = 0; i < _infos.length; i++) {
                        if (box.intersectsBox(_infos[i].box)) {
                            infos.push(_infos[i]);
                        }
                    }
                    if (infos.length < 1) continue;

                    const node = new Node();
                    node.set([], box);
                    _node.addChild(node);

                    this.createSubTree(_level, node, infos);
                }
            }
        }
    }

    // Octree 八分木
    private spacePlace(node: Node): THREE.Box3[] {
        const bbox = node.box;
        const min = bbox.min;
        const max = bbox.max;
        const halfSizeX = new THREE.Vector3(bbox.getSize(new THREE.Vector3()).divideScalar(2).x, 0, 0);
        const halfSizeY = new THREE.Vector3(0, bbox.getSize(new THREE.Vector3()).divideScalar(2).y, 0);
        const halfSizeZ = new THREE.Vector3(0, 0, bbox.getSize(new THREE.Vector3()).divideScalar(2).z);
        const center = bbox.getCenter(new THREE.Vector3());

        // 空間を８つに分割してセット
        const sp = [];
        sp.push( new THREE.Box3(min, center) );
        sp.push( new THREE.Box3(min.clone().add(halfSizeX), center.clone().add(halfSizeX)) );
        sp.push( new THREE.Box3(min.clone().add(halfSizeY), center.clone().add(halfSizeY)) );
        sp.push( new THREE.Box3(min.clone().add(halfSizeZ), center.clone().add(halfSizeZ)) );
        sp.push( new THREE.Box3(min.clone().add(halfSizeX).clone().add(halfSizeY), center.clone().add(halfSizeX).clone().add(halfSizeY)) );
        sp.push( new THREE.Box3(min.clone().add(halfSizeY).clone().add(halfSizeZ), center.clone().add(halfSizeY).clone().add(halfSizeZ)) );
        sp.push( new THREE.Box3(min.clone().add(halfSizeZ).clone().add(halfSizeX), center.clone().add(halfSizeZ).clone().add(halfSizeX)) );
        sp.push( new THREE.Box3(center, max) );
        return sp;
    }

    public draw(bbox: THREE.Box3) {
        const min = bbox.min;
        const max = bbox.max;
        const halfSizeX = new THREE.Vector3(bbox.getSize(new THREE.Vector3()).divideScalar(2).x, 0, 0);
        const halfSizeY = new THREE.Vector3(0, bbox.getSize(new THREE.Vector3()).divideScalar(2).y, 0);
        const halfSizeZ = new THREE.Vector3(0, 0, bbox.getSize(new THREE.Vector3()).divideScalar(2).z);
        const center = bbox.getCenter(new THREE.Vector3());
        const color = this.getColor();
        ThreeView.get().wcItemmgr.addOtherShapes(new THREE.Box3Helper( new THREE.Box3(min, center), color ));
        ThreeView.get().wcItemmgr.addOtherShapes(new THREE.Box3Helper( new THREE.Box3(min.clone().add(halfSizeX), center.clone().add(halfSizeX)), color ));
        ThreeView.get().wcItemmgr.addOtherShapes(new THREE.Box3Helper( new THREE.Box3(min.clone().add(halfSizeY), center.clone().add(halfSizeY)), color ));
        ThreeView.get().wcItemmgr.addOtherShapes(new THREE.Box3Helper( new THREE.Box3(min.clone().add(halfSizeZ), center.clone().add(halfSizeZ)), color ));
        ThreeView.get().wcItemmgr.addOtherShapes(new THREE.Box3Helper( new THREE.Box3(min.clone().add(halfSizeX).clone().add(halfSizeY), center.clone().add(halfSizeX).clone().add(halfSizeY)), color ));
        ThreeView.get().wcItemmgr.addOtherShapes(new THREE.Box3Helper( new THREE.Box3(min.clone().add(halfSizeY).clone().add(halfSizeZ), center.clone().add(halfSizeY).clone().add(halfSizeZ)), color ));
        ThreeView.get().wcItemmgr.addOtherShapes(new THREE.Box3Helper( new THREE.Box3(min.clone().add(halfSizeZ).clone().add(halfSizeX), center.clone().add(halfSizeZ).clone().add(halfSizeX)), color ));
        ThreeView.get().wcItemmgr.addOtherShapes(new THREE.Box3Helper( new THREE.Box3(center, max), color ));
    }

    private getColor(): THREE.Color {
        const color = (Math.random() * 0xFFFFFF | 0).toString(16);
        const randomColor = "#" + ("000000" + color).slice(-6);
        return new THREE.Color(randomColor);
    }

}
